import { useCallback, useEffect, useState } from "react";

import PropTypes from "prop-types";

import { CompareArrows as CompareArrowsIcon } from "@mui/icons-material";
import { Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { apiClient, formatNumber, getErrorMessage } from "@/features/shared";
import { useSnackbar } from "@/features/ui";
import { WarehouseStockTransactionsDialog } from "@/features/warehouse-stock-transactions";
import { WarehouseSelect } from "@/features/warehouses";

export const WarehouseStockByWarehouses = ({
  initialWarehouseId = null,
  initialWarehouseName = null,
  onWarehouseChange,
}) => {
  const { showSnackbar } = useSnackbar();
  const [stocksByWarehouses, setStocksByWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  // Seed the warehouse from a deep link (?warehouseId=...). Only the id is
  // required to load stock; the name is display-only for the select label.
  // Guarded so that updating the URL *from* a manual selection (same id) keeps
  // the richer selected object instead of clobbering it with a minimal one.
  useEffect(() => {
    if (!initialWarehouseId) return;
    setSelectedWarehouse((prev) =>
      prev?.id === initialWarehouseId
        ? prev
        : { id: initialWarehouseId, name: initialWarehouseName || "" }
    );
  }, [initialWarehouseId, initialWarehouseName]);
  const [selectedProductVariant, setSelectedProductVariant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState([]);
  const [rowCountState, setRowCountState] = useState(0);

  const loadStocksByWarehouses = useCallback(
    async (params = {}) => {
      if (!selectedWarehouse) return;

      setLoading(true);
      try {
        // Build sort parameter
        const currentSort = params.sortModel ?? sortModel;
        let sortParam = null;
        if (currentSort.length > 0) {
          const { field, sort } = currentSort[0];
          sortParam = sort === "desc" ? `-${field}` : field;
        }

        const {
          data: { data, pageInfo },
        } = await apiClient.get(`warehouses/${selectedWarehouse.id}/stocks`, {
          params: {
            page: params.page ?? paginationModel.page,
            size: params.pageSize ?? paginationModel.pageSize,
            ...(sortParam && { sort: sortParam }),
          },
        });
        setStocksByWarehouses(data);
        setRowCountState(pageInfo.totalItems);
      } catch (error) {
        showSnackbar(getErrorMessage(error, "Gagal memuat stok gudang"));
      } finally {
        setLoading(false);
      }
    },
    [
      selectedWarehouse,
      paginationModel.page,
      paginationModel.pageSize,
      sortModel,
      showSnackbar,
    ]
  );

  // Load data when warehouse changes
  useEffect(() => {
    if (selectedWarehouse) {
      loadStocksByWarehouses();
    } else {
      setStocksByWarehouses([]);
      setRowCountState(0);
    }
  }, [selectedWarehouse, loadStocksByWarehouses]);

  // Handle DataGrid state changes (pagination, sorting, filtering)
  const handlePaginationModelChange = useCallback(
    (newPaginationModel) => {
      setPaginationModel(newPaginationModel);
      // Load data with new pagination
      loadStocksByWarehouses({
        page: newPaginationModel.page,
        pageSize: newPaginationModel.pageSize,
        sortModel,
      });
    },
    [loadStocksByWarehouses, sortModel]
  );

  const handleSortModelChange = useCallback(
    (newSortModel) => {
      setSortModel(newSortModel);
      // Load data with new sorting (reset to first page)
      loadStocksByWarehouses({
        page: 0,
        pageSize: paginationModel.pageSize,
        sortModel: newSortModel,
      });
      // Reset pagination to first page when sorting changes
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [loadStocksByWarehouses, paginationModel.pageSize]
  );

  return (
    <Stack spacing={3}>
      <Grid container sx={{ justifyContent: "flex-end" }}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <WarehouseSelect
            value={selectedWarehouse}
            onChange={(warehouse) => {
              setSelectedWarehouse(warehouse);
              onWarehouseChange?.(warehouse);
            }}
            TextFieldProps={{
              InputProps: {
                placeholder: "Pilih Gudang",
              },
            }}
          />
        </Grid>
      </Grid>
      <DataGrid
        getRowHeight={() => "auto"}
        columns={[
          {
            field: "name",
            headerName: "Nama Produk Varian",
            flex: 3,
            sortable: false,
          },
          {
            field: "sku",
            headerName: "SKU",
            flex: 3,
            sortable: false,
          },
          {
            field: "quantity",
            headerName: "Kuantitas",
            type: "number",
            flex: 2,
            sortable: false,
            valueFormatter: (value) => formatNumber(value),
          },
          {
            field: "unitOfMeasurement",
            headerName: "Satuan",
            sortable: false,
            flex: 1,
            valueGetter: (_, row) =>
              row.product?.unitOfMeasurement?.name || "-",
          },
          {
            field: "actions",
            headerName: "Aksi",
            type: "actions",
            width: 80,
            getActions: (params) => [
              <GridActionsCellItem
                key="view-transactions"
                icon={<CompareArrowsIcon />}
                label="Lihat Transaksi"
                onClick={() => setSelectedProductVariant(params.row)}
              />,
            ],
          },
        ]}
        rows={stocksByWarehouses}
        loading={loading}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={rowCountState}
        onPaginationModelChange={handlePaginationModelChange}
        sorting
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        disableSelectionOnClick
      />

      {selectedProductVariant && (
        <WarehouseStockTransactionsDialog
          warehouse={selectedWarehouse}
          productVariant={selectedProductVariant}
          onClose={() => setSelectedProductVariant(null)}
        />
      )}
    </Stack>
  );
};

WarehouseStockByWarehouses.propTypes = {
  initialWarehouseId: PropTypes.string,
  initialWarehouseName: PropTypes.string,
  onWarehouseChange: PropTypes.func,
};
